import { useLayoutEffect } from "react";

/**
 * The per-page bootstrap: scroll to the top, re-initialise the Webflow
 * interaction engine, and undo any transform it stamped on the nav container.
 *
 * Every page needs this, and until this hook existed every page carried its own
 * copy — nine of them, each subtly different. That mattered more than the
 * duplication did, because the guarantee below only holds if it is written once.
 *
 * ── Why `useLayoutEffect` ─────────────────────────────────────────────────
 * This is the whole anti-flicker mechanism, and it replaces a black loading
 * overlay that used to hide the same problem behind a timer.
 *
 * The Webflow engine stamps `opacity: 0` and 3D transforms onto every
 * `data-w-id` element when `animations.init()` runs — that is how its scroll
 * reveals start hidden. `init()` is fully synchronous: by the time it returns,
 * every element in the committed DOM has its initial state.
 *
 * Run that from a `useEffect` and it lands *after* the browser has painted, so
 * for exactly one frame the page renders un-stamped — headings and images flash
 * in at full opacity and then snap away. Run it from a `useLayoutEffect` and it
 * lands inside React's commit, before paint, so the frame never exists. Child
 * effects run before the parent's, so the page DOM is fully committed here.
 *
 * This is strictly safer than the opacity gate it replaces: a gate is a state
 * the page can get *stuck* in (the old overlay dismissed itself on a
 * `transitionend` that, if it never fired, left the page blank forever), while
 * correct ordering has no stuck state at all.
 *
 * ── The fail-safe ─────────────────────────────────────────────────────────
 * `window._Agapae` is a vendored static asset. It is allowed to be missing (a
 * partial local setup) or to throw, so the engine call is the only thing inside
 * the try. Nothing below it depends on the engine having run. An engine failure
 * costs animations — never a readable page.
 *
 * @param {object}  [options]
 * @param {boolean} [options.enabled=true] False while a page has nothing
 *   mounted to initialise — `Guide` early-returns a redirect for an unknown
 *   slug, and there is no DOM worth stamping in that render.
 */
export default function usePageShell({ enabled = true } = {}) {
  useLayoutEffect(() => {
    if (!enabled) return;

    // Before init(), not after: the engine's SCROLLING_IN_VIEW actions compute
    // their resting state from the current scroll offset, so initialising at
    // the previous page's offset stamps the wrong values.
    window.scrollTo(0, 0);

    try {
      const webflow = window._Agapae;
      const animations = webflow?.require?.("animations");
      if (webflow && animations) {
        webflow.destroy();
        webflow.ready();
        animations.init();
        webflow.require("slider")?.redraw?.();
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[usePageShell] interaction engine failed", error);
      }
    }

    // Reveal the page body, and undo the transform the engine stamps on the nav
    // container.
    //
    // `.main` is not merely "not yet animated" — `init()` stamps `opacity: 0`
    // on it and the reveal belongs to a page-load trigger that fired long
    // before React mounted, so re-initialising the engine leaves the body
    // hidden with nothing left to un-hide it. On desktop it stays at 0
    // indefinitely; on mobile the interaction is not configured, so it does
    // not. That asymmetry is why this is written rather than assumed: without
    // it the homepage is blank above 768px and fine below it.
    //
    // The page it reveals is already fully stamped, so this is the frame the
    // visitor sees — not a fade begun 1.6s after a black overlay, which is what
    // this replaced.
    document.querySelectorAll(".main").forEach((main) => {
      main.style.opacity = "1";
    });

    const navContainer = document.querySelector(".nav-container");
    if (navContainer) navContainer.style.transform = "translate3d(0, 0, 0)";
  }, [enabled]);
}
